import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { InnerPageTopNav } from '~/components/inner-page-top-nav'
import { makeTranslatedTitle } from '~/helpers/pages'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { LegalRichTextEditor } from '~/routes/shop.$shopSlug.legal/components/legal-rich-text-editor'
import { requireShopAndItsOptionalBuyer } from '~/services/auth-shop.server'
import { cn } from '~/utils/classnames'
import { DATE_FORMATS, formatDate } from '~/utils/date'
import { generateShopLogoFormats } from '~/utils/image'
import { generateSeoMetaTags } from '~/utils/seo-helpers'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop } = await requireShopAndItsOptionalBuyer(loaderArgs.request)

  const shopLogos = await generateShopLogoFormats(shop.themeColor)

  return json({
    shop,
    shopLogos,
    title: await makeTranslatedTitle('page.settings.legal.legal_info'),
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.shop) {
    return [{ title: data?.title }]
  }

  return generateSeoMetaTags(
    {
      title: data.title,
    },
    data.shop,
    data.shopLogos?.png,
    data.shopLogos?.ico
  )
}

type PoliciesKey = 'termsAndConditions' | 'privacyPolicy' | 'returnPolicy'

export default function LegalPage() {
  const shop = useShop()

  return (
    <div className="bg-background text-foreground relative flex min-h-full flex-col rounded-t-[inherit]">
      <div className="flex h-full flex-col justify-between">
        <InnerPageTopNav>
          <Link to={makeShopOwnPath(shop, '/')} className="text-md font-medium">
            {shop.name}
          </Link>
        </InnerPageTopNav>
        <LegalPageContent />
      </div>
    </div>
  )
}

function LegalPageContent() {
  const { t } = useTranslation()
  const seller = useOptionalSeller()
  const { shop } = useLoaderData<typeof loader>()

  const sections: {
    id: PoliciesKey
    label: string
    placeholderContent: string
  }[] = [
    {
      id: 'termsAndConditions',
      label: t('page.settings.legal.terms_and_conditions'),
      placeholderContent: t('page.settings.legal.terms_default_content'),
    },
    {
      id: 'privacyPolicy',
      label: t('page.settings.legal.privacy_policy'),
      placeholderContent: t('page.settings.legal.privacy_default_content'),
    },
    {
      id: 'returnPolicy',
      label: t('page.settings.legal.return_policy'),
      placeholderContent: t('page.settings.legal.return_default_content'),
    },
  ]

  return (
    <div className="mx-auto mb-20 w-full max-w-[800px] px-4 pt-6 @md:pt-24">
      {sections.map((section) => {
        const content = shop[section.id]

        return (
          <div
            key={section.id}
            className={cn(
              'mb-12 flex flex-col gap-4',
              '[&_a]:text-accent-element'
            )}
          >
            <div>
              <h4 className="text-xl font-medium @md:text-2xl">
                {section.label}
              </h4>

              <div className="text-sm opacity-64">
                <span>Last revised on: </span>
                <span>{formatDate(new Date(), DATE_FORMATS.shortmonth)}</span>
              </div>
            </div>

            {seller ? (
              <LegalRichTextEditor
                fieldName={section.id}
                initialValue={content || section.placeholderContent}
                placeholder={section.placeholderContent}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: content || section.placeholderContent,
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
